/**
 * Canonical Puerto Rico municipality identifiers and representative centers.
 * Centers are 2024 Census Gazetteer INTPTLAT/INTPTLONG values for Puerto Rico
 * county equivalents. Mayagüez uses the same Gazetteer's zona urbana point because the
 * municipio point is displaced by its offshore islands:
 * https://www.census.gov/geographies/reference-files/time-series/geo/gazetteer-files.2024.html
 * They are only initial map positions, never exact service locations.
 */
const PUERTO_RICO_BOUNDS = {
  minLat: 17.8,
  maxLat: 18.6,
  minLng: -68.1,
  maxLng: -65.1,
};

const PUERTO_RICO_CENTER = { lat: 18.2208, lng: -66.5901 };
const DEFAULT_MUNICIPALITY_ZOOM = 12;
const CUSTOMER_SERVICE_LOCATION_ADDRESS_MAX_LENGTH = 300;
const CUSTOMER_SERVICE_LOCATION_APPROXIMATE_ADDRESS_MAX_LENGTH = 250;

const municipality = (id, name, lat, lng, zoom = DEFAULT_MUNICIPALITY_ZOOM) => ({
  id,
  name,
  center: { lat, lng },
  zoom,
});

const PUERTO_RICO_MUNICIPALITIES = [
  municipality('adjuntas', 'Adjuntas', 18.181611, -66.758165),
  municipality('aguada', 'Aguada', 18.375673, -67.185745),
  municipality('aguadilla', 'Aguadilla', 18.480191, -67.143762),
  municipality('aguas-buenas', 'Aguas Buenas', 18.256524, -66.128496),
  municipality('aibonito', 'Aibonito', 18.130723, -66.26446),
  municipality('anasco', 'Añasco', 18.286905, -67.131282),
  municipality('arecibo', 'Arecibo', 18.434004, -66.674994),
  municipality('arroyo', 'Arroyo', 17.97206, -66.041945),
  municipality('barceloneta', 'Barceloneta', 18.469989, -66.55823),
  municipality('barranquitas', 'Barranquitas', 18.200018, -66.309282),
  municipality('bayamon', 'Bayamón', 18.34942, -66.168626),
  municipality('cabo-rojo', 'Cabo Rojo', 17.997138, -67.180862),
  municipality('caguas', 'Caguas', 18.211109, -66.050964),
  municipality('camuy', 'Camuy', 18.445469, -66.863134),
  municipality('canovanas', 'Canóvanas', 18.330044, -65.886021),
  municipality('carolina', 'Carolina', 18.396776, -65.968778),
  municipality('catano', 'Cataño', 18.444614, -66.148819),
  municipality('cayey', 'Cayey', 18.103624, -66.151667),
  municipality('ceiba', 'Ceiba', 18.253265, -65.620765),
  municipality('ciales', 'Ciales', 18.295913, -66.515588),
  municipality('cidra', 'Cidra', 18.174391, -66.161618),
  municipality('coamo', 'Coamo', 18.1038, -66.357586),
  municipality('comerio', 'Comerío', 18.225032, -66.219481),
  municipality('corozal', 'Corozal', 18.30391, -66.326179),
  municipality('culebra', 'Culebra', 18.326611, -65.307771),
  municipality('dorado', 'Dorado', 18.474376, -66.261973),
  municipality('fajardo', 'Fajardo', 18.386378, -65.588454),
  municipality('florida', 'Florida', 18.368659, -66.562734),
  municipality('guanica', 'Guánica', 17.948209, -66.922935),
  municipality('guayama', 'Guayama', 17.973929, -66.137467),
  municipality('guayanilla', 'Guayanilla', 18.005338, -66.798293),
  municipality('guaynabo', 'Guaynabo', 18.344357, -66.114056),
  municipality('gurabo', 'Gurabo', 18.272581, -65.981177),
  municipality('hatillo', 'Hatillo', 18.441141, -66.79821),
  municipality('hormigueros', 'Hormigueros', 18.134695, -67.116199),
  municipality('humacao', 'Humacao', 18.135403, -65.786229),
  municipality('isabela', 'Isabela', 18.483998, -67.013978),
  municipality('jayuya', 'Jayuya', 18.211152, -66.58687),
  municipality('juana-diaz', 'Juana Díaz', 17.997974, -66.490496),
  municipality('juncos', 'Juncos', 18.224133, -65.908542),
  municipality('lajas', 'Lajas', 17.978511, -67.040111),
  municipality('lares', 'Lares', 18.276925, -66.869587),
  municipality('las-marias', 'Las Marías', 18.227594, -66.97758),
  municipality('las-piedras', 'Las Piedras', 18.187148, -65.871189),
  municipality('loiza', 'Loíza', 18.445503, -65.942649),
  municipality('luquillo', 'Luquillo', 18.367996, -65.709904),
  municipality('manati', 'Manatí', 18.444803, -66.493137),
  municipality('maricao', 'Maricao', 18.173956, -66.935547),
  municipality('maunabo', 'Maunabo', 17.999786, -65.896403),
  municipality('mayaguez', 'Mayagüez', 18.201221, -67.14178),
  municipality('moca', 'Moca', 18.377637, -67.079574),
  municipality('morovis', 'Morovis', 18.318017, -66.420392),
  municipality('naguabo', 'Naguabo', 18.21107, -65.735749),
  municipality('naranjito', 'Naranjito', 18.28992, -66.25344),
  municipality('orocovis', 'Orocovis', 18.218733, -66.436899),
  municipality('patillas', 'Patillas', 18.000311, -65.986642),
  municipality('penuelas', 'Peñuelas', 18.026618, -66.728125),
  municipality('ponce', 'Ponce', 18.001717, -66.606662),
  municipality('quebradillas', 'Quebradillas', 18.466357, -66.927603),
  municipality('rincon', 'Rincón', 18.33898, -67.250771),
  municipality('rio-grande', 'Río Grande', 18.376369, -65.798434),
  municipality('sabana-grande', 'Sabana Grande', 18.084483, -66.947609),
  municipality('salinas', 'Salinas', 17.971485, -66.262252),
  municipality('san-german', 'San Germán', 18.1078, -67.037263),
  municipality('san-juan', 'San Juan', 18.422249, -66.069081),
  municipality('san-lorenzo', 'San Lorenzo', 18.147107, -65.976167),
  municipality('san-sebastian', 'San Sebastián', 18.331064, -66.969064),
  municipality('santa-isabel', 'Santa Isabel', 17.952922, -66.387588),
  municipality('toa-alta', 'Toa Alta', 18.364556, -66.244669),
  municipality('toa-baja', 'Toa Baja', 18.45691, -66.193193),
  municipality('trujillo-alto', 'Trujillo Alto', 18.335387, -66.003787),
  municipality('utuado', 'Utuado', 18.270865, -66.702989),
  municipality('vega-alta', 'Vega Alta', 18.43622, -66.336412),
  municipality('vega-baja', 'Vega Baja', 18.455128, -66.397883),
  municipality('vieques', 'Vieques', 18.125418, -65.432474),
  municipality('villalba', 'Villalba', 18.130718, -66.472244),
  municipality('yabucoa', 'Yabucoa', 18.059858, -65.859871),
  municipality('yauco', 'Yauco', 18.085669, -66.857901),
];

const municipalitiesById = new Map(PUERTO_RICO_MUNICIPALITIES.map(item => [item.id, item]));

const getPuertoRicoMunicipality = id => municipalitiesById.get(id) || null;
const getPuertoRicoMunicipalityName = id => getPuertoRicoMunicipality(id)?.name || null;
const getPuertoRicoMunicipalityMapView = id => {
  const item = getPuertoRicoMunicipality(id);
  return item ? { center: item.center, zoom: item.zoom } : null;
};
const isPuertoRicoMunicipalityId = id => typeof id === 'string' && municipalitiesById.has(id);
const getPuertoRicoMunicipalityOptions = (allowedIds = null) => {
  const allowed = Array.isArray(allowedIds) ? new Set(allowedIds) : null;
  return PUERTO_RICO_MUNICIPALITIES.filter(item => !allowed || allowed.has(item.id)).map(item => ({
    key: item.id,
    value: item.id,
    option: item.id,
    label: item.name,
  }));
};
const isValidPuertoRicoCoordinates = origin => {
  const { lat, lng } = origin || {};
  return (
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    lat >= PUERTO_RICO_BOUNDS.minLat &&
    lat <= PUERTO_RICO_BOUNDS.maxLat &&
    lng >= PUERTO_RICO_BOUNDS.minLng &&
    lng <= PUERTO_RICO_BOUNDS.maxLng
  );
};

module.exports = {
  PUERTO_RICO_BOUNDS,
  PUERTO_RICO_CENTER,
  DEFAULT_MUNICIPALITY_ZOOM,
  CUSTOMER_SERVICE_LOCATION_ADDRESS_MAX_LENGTH,
  CUSTOMER_SERVICE_LOCATION_APPROXIMATE_ADDRESS_MAX_LENGTH,
  PUERTO_RICO_MUNICIPALITIES,
  getPuertoRicoMunicipality,
  getPuertoRicoMunicipalityName,
  getPuertoRicoMunicipalityMapView,
  isPuertoRicoMunicipalityId,
  getPuertoRicoMunicipalityOptions,
  isValidPuertoRicoCoordinates,
};
